(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  const editorialCleanups = new WeakMap();

  const elementsWithin = (root, selector) => {
    const elements = [...root.querySelectorAll(selector)];
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    return elements;
  };

  const setInteractiveState = (element, active) => {
    element.classList.toggle('is-active', active);
    element.setAttribute('aria-hidden', String(!active));
    if (element.matches('a, button')) element.tabIndex = active ? 0 : -1;
    element.querySelectorAll('a, button').forEach((interactive) => {
      interactive.tabIndex = active ? 0 : -1;
    });
  };

  const initHero = (hero) => {
    if (initialized.has(hero)) return;
    initialized.add(hero);

    const stage = hero.querySelector('.fg-hero__stage');
    const wordmark = hero.querySelector('[data-fg-hero-wordmark]');
    const desktopHover = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    let scrollFrame;

    const clearWordmarkHover = () => hero.classList.remove('is-wordmark-hovered');
    const updateWordmarkHover = (event) => {
      if (!wordmark || !desktopHover.matches) {
        clearWordmarkHover();
        return;
      }

      const bounds = wordmark.getBoundingClientRect();
      const isInsideWordmark = event.clientX >= bounds.left
        && event.clientX <= bounds.right
        && event.clientY >= bounds.top
        && event.clientY <= bounds.bottom;
      const activeCard = hero.querySelector('.fg-hero__card-selector .fg-selector__card.is-active');
      const cardBounds = activeCard?.getBoundingClientRect();
      const isOverCard = cardBounds
        && event.clientX >= cardBounds.left
        && event.clientX <= cardBounds.right
        && event.clientY >= cardBounds.top
        && event.clientY <= cardBounds.bottom;
      const alreadyHovered = hero.classList.contains('is-wordmark-hovered');
      hero.classList.toggle('is-wordmark-hovered', isInsideWordmark && (!isOverCard || alreadyHovered));
    };

    hero.addEventListener('pointermove', updateWordmarkHover);
    hero.addEventListener('pointerleave', clearWordmarkHover);
    desktopHover.addEventListener?.('change', clearWordmarkHover);

    if (stage) {
      const updateScroll = () => {
        scrollFrame = undefined;
        const bounds = hero.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, -bounds.top / Math.max(bounds.height * .72, 1)));
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        // Keep the selector shelf locked to the deck while the hero scales on scroll.
        // A positive Y parallax made the shelf drift over the FATHERGLORIFIED wordmark.
        stage.style.setProperty('--fg-scroll-y', '0rem');
        stage.style.setProperty('--fg-scroll-scale', String(1 - (progress * (mobile ? .02 : .03))));
      };
      const requestScrollUpdate = () => {
        if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScroll);
      };
      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
      hero.closest('.page-wrapper')?.addEventListener('scroll', requestScrollUpdate, { passive: true });
      updateScroll();
    }

    hero.querySelector('[data-fg-scroll-next]')?.addEventListener('click', (event) => {
      const wrapper = hero.closest('.shopify-section');
      const next = wrapper?.nextElementSibling;
      if (!next) return;
      event.preventDefault();
      next.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    });
  };

  const initEditorial = (editorial) => {
    if (initialized.has(editorial)) return;
    initialized.add(editorial);

    const slides = [...editorial.querySelectorAll('[data-fg-editorial-slide]')];
    const copies = [...editorial.querySelectorAll('[data-fg-editorial-copy]')];
    const mediaCopies = [...editorial.querySelectorAll('[data-fg-editorial-media-copy]')];
    const controls = [...editorial.querySelectorAll('[data-fg-editorial-story-control]')];
    const media = editorial.querySelector('.fg-editorial__media');
    const stories = [...new Set(copies.map((copy) => copy.dataset.fgEditorialStory))]
      .sort((a, b) => Number(a) - Number(b));
    const imageDelay = Number(editorial.dataset.autoplay) || 5000;
    const storyDelay = Math.max(Number(editorial.dataset.storyAutoplay) || 10000, imageDelay * 2);
    const activeStory = copies.find((copy) => copy.classList.contains('is-active'))?.dataset.fgEditorialStory;
    let storyIndex = Math.max(0, stories.indexOf(activeStory));
    let imageIndex = 0;
    let storyStartedAt = Date.now();
    let timer;
    let pointerStart;

    const normalizeStoryIndex = (index) => (index + stories.length) % stories.length;
    const storySlides = (story) => slides.filter((slide) => slide.dataset.fgEditorialStory === story);

    const show = (nextStoryIndex, nextImageIndex = 0) => {
      storyIndex = normalizeStoryIndex(nextStoryIndex);
      const story = stories[storyIndex];
      const availableSlides = storySlides(story);
      imageIndex = availableSlides.length ? (nextImageIndex + availableSlides.length) % availableSlides.length : 0;

      slides.forEach((slide) => {
        const active = slide.dataset.fgEditorialStory === story
          && Number(slide.dataset.fgEditorialImage) === imageIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      copies.forEach((copy) => {
        setInteractiveState(copy, copy.dataset.fgEditorialStory === story);
      });
      mediaCopies.forEach((mediaCopy) => {
        const active = mediaCopy.dataset.fgEditorialStory === story
          && mediaCopy.dataset.fgEditorialImage.split(/\s+/).includes(String(imageIndex));
        mediaCopy.classList.toggle('is-active', active);
        mediaCopy.setAttribute('aria-hidden', String(!active));
      });
      controls.forEach((control) => {
        const active = Number(control.dataset.index) === storyIndex;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-current', String(active));
      });
    };

    const stop = () => {
      window.clearTimeout(timer);
      timer = undefined;
    };

    const schedule = () => {
      stop();
      if (stories.length < 2 || document.hidden || !editorial.isConnected) return;
      const story = stories[storyIndex];
      const availableSlides = storySlides(story);
      const elapsed = Date.now() - storyStartedAt;
      const storyRemaining = Math.max(0, storyDelay - elapsed);
      const wait = availableSlides.length > 1 && imageIndex === 0
        ? Math.min(imageDelay, storyRemaining)
        : storyRemaining;

      timer = window.setTimeout(() => {
        const currentElapsed = Date.now() - storyStartedAt;
        if (currentElapsed >= storyDelay - 80) {
          storyStartedAt = Date.now();
          show(storyIndex + 1, 0);
        } else if (availableSlides.length > 1) {
          show(storyIndex, imageIndex + 1);
        }
        schedule();
      }, Math.max(80, wait));
    };

    const goToStory = (nextStoryIndex) => {
      storyStartedAt = Date.now();
      show(nextStoryIndex, 0);
      schedule();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) stop();
      else {
        storyStartedAt = Date.now();
        show(storyIndex, 0);
        schedule();
      }
    };

    const handlePointerDown = (event) => {
      if (!event.isPrimary) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };

    const handlePointerUp = (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = undefined;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      goToStory(storyIndex + (deltaX < 0 ? 1 : -1));
    };

    const handlePointerCancel = () => {
      pointerStart = undefined;
    };

    const handlePaginationKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      goToStory(storyIndex + direction);
      controls[storyIndex]?.focus({ preventScroll: true });
    };

    const handleControlClick = (event) => {
      goToStory(Number(event.currentTarget.dataset.index));
    };

    controls.forEach((control) => {
      control.addEventListener('click', handleControlClick);
      control.addEventListener('keydown', handlePaginationKeydown);
    });
    media?.addEventListener('pointerdown', handlePointerDown);
    media?.addEventListener('pointerup', handlePointerUp);
    media?.addEventListener('pointercancel', handlePointerCancel);

    show(storyIndex, 0);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    editorialCleanups.set(editorial, () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      controls.forEach((control) => {
        control.removeEventListener('click', handleControlClick);
        control.removeEventListener('keydown', handlePaginationKeydown);
      });
      media?.removeEventListener('pointerdown', handlePointerDown);
      media?.removeEventListener('pointerup', handlePointerUp);
      media?.removeEventListener('pointercancel', handlePointerCancel);
    });
    schedule();
  };

  const initSelector = (selector) => {
    if (initialized.has(selector)) return;
    initialized.add(selector);

    const tabStrip = selector.querySelector('.fg-selector__tabs');
    const swipeDeck = selector.querySelector('[data-fg-swipe-deck]');
    const previousButton = selector.querySelector('[data-fg-selector-previous]');
    const nextButton = selector.querySelector('[data-fg-selector-next]');
    const productPanels = [...selector.querySelectorAll('[data-fg-selector-products]')];
    const comingSoonRank = (element) => element.dataset.fgComingSoon === 'true' ? 1 : 0;
    const cards = [...selector.querySelectorAll('[data-fg-selector-card]')]
      .sort((first, second) => comingSoonRank(first) - comingSoonRank(second));
    const tabs = [...selector.querySelectorAll('[data-fg-selector-tab]')]
      .sort((first, second) => comingSoonRank(first) - comingSoonRank(second));

    const swipeControls = swipeDeck?.querySelector('.fg-hero__swipe-controls');
    cards.forEach((card, index) => {
      card.dataset.index = String(index);
      swipeDeck?.insertBefore(card, swipeControls || null);
    });
    tabs.forEach((tab, index) => {
      tab.dataset.index = String(index);
      tabStrip?.appendChild(tab);
    });

    const mobileArtworkViewport = window.matchMedia('(max-width: 749px)');
    let activeIndex = 0;
    let dragState;
    let suppressCardClickUntil = 0;
    let tabSnapTimer;
    let tabMotionTimer;
    let deckMotionTimer;
    let tabCarouselReady = false;

    let carouselTabs = tabs;
    let tabCycles;

    if (tabStrip && tabs.length > 1) {
      const cloneCycle = (position) => tabs.map((tab) => {
        const clone = tab.cloneNode(true);
        clone.dataset.fgSelectorClone = position;
        clone.removeAttribute('role');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        return clone;
      });
      const before = cloneCycle('before');
      const after = cloneCycle('after');
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();
      before.forEach((tab) => beforeFragment.appendChild(tab));
      after.forEach((tab) => afterFragment.appendChild(tab));
      tabStrip.prepend(beforeFragment);
      tabStrip.append(afterFragment);
      tabCycles = { before, current: tabs, after };
      carouselTabs = [...before, ...tabs, ...after];
    }

    const normalizeIndex = (index) => (index + cards.length) % cards.length;

    const hydrateMobileArtwork = (index) => {
      if (!mobileArtworkViewport.matches || !cards.length) return;
      const source = cards[normalizeIndex(index)]?.querySelector('[data-fg-mobile-srcset]');
      const srcset = source?.dataset.fgMobileSrcset;
      if (!source || !srcset) return;
      source.setAttribute('srcset', srcset);
      source.removeAttribute('data-fg-mobile-srcset');
    };

    const warmMobileArtworkRing = () => {
      hydrateMobileArtwork(activeIndex);
      hydrateMobileArtwork(activeIndex - 1);
      hydrateMobileArtwork(activeIndex + 1);
    };

    warmMobileArtworkRing();
    mobileArtworkViewport.addEventListener?.('change', warmMobileArtworkRing);

    const clearPreviews = () => {
      cards.forEach((card) => card.classList.remove('is-swipe-preview'));
    };

    const clearSwipeStyles = (card) => {
      card.classList.remove('is-dragging');
      card.style.removeProperty('--fg-swipe-x');
      card.style.removeProperty('--fg-swipe-y');
      card.style.removeProperty('--fg-swipe-rotate');
    };

    const clearTrayDrag = () => {
      if (!tabStrip) return;
      tabStrip.classList.remove('is-tray-dragging');
      tabStrip.style.removeProperty('--fg-tray-drag-x');
    };

    const updateTrayDrag = (deltaX) => {
      if (!tabStrip || reducedMotion.matches) return;
      const shift = Math.max(-18, Math.min(18, deltaX * .065));
      tabStrip.classList.add('is-tray-dragging');
      tabStrip.style.setProperty('--fg-tray-drag-x', `${shift.toFixed(2)}px`);
    };

    const animateTray = (direction) => {
      if (!tabStrip || !direction || reducedMotion.matches) return;
      window.clearTimeout(tabMotionTimer);
      tabStrip.classList.remove('is-rotating-next', 'is-rotating-previous');
      void tabStrip.offsetWidth;
      tabStrip.classList.add(direction > 0 ? 'is-rotating-next' : 'is-rotating-previous');
      tabMotionTimer = window.setTimeout(() => {
        tabStrip.classList.remove('is-rotating-next', 'is-rotating-previous');
      }, 560);
    };

    const scrollTabToCenter = (tab, behavior = 'smooth') => {
      if (!tabStrip || !tab) return;
      const desiredScroll = tab.offsetLeft - ((tabStrip.clientWidth - tab.offsetWidth) / 2);
      if (typeof tabStrip.scrollTo === 'function') {
        tabStrip.scrollTo({ left: desiredScroll, behavior });
      } else {
        tabStrip.scrollLeft = desiredScroll;
      }
    };

    const centerActiveTab = (direction = 0, previousIndex = activeIndex, instant = false) => {
      const primaryTab = tabs[activeIndex];
      if (!tabStrip || !primaryTab) return;

      window.clearTimeout(tabSnapTimer);
      let targetTab = primaryTab;
      let needsCycleSnap = false;

      if (tabCycles && direction > 0 && previousIndex > activeIndex) {
        targetTab = tabCycles.after[activeIndex];
        needsCycleSnap = true;
      } else if (tabCycles && direction < 0 && previousIndex < activeIndex) {
        targetTab = tabCycles.before[activeIndex];
        needsCycleSnap = true;
      }

      scrollTabToCenter(targetTab, instant || reducedMotion.matches ? 'auto' : 'smooth');

      if (needsCycleSnap && !instant && !reducedMotion.matches) {
        tabSnapTimer = window.setTimeout(() => scrollTabToCenter(primaryTab, 'auto'), 540);
      }
    };

    const select = (nextIndex, moveFocus = false, requestedDirection = 0) => {
      if (!cards.length) return;
      const previousIndex = activeIndex;
      activeIndex = normalizeIndex(nextIndex);
      const forwardDistance = normalizeIndex(activeIndex - previousIndex);
      const backwardDistance = normalizeIndex(previousIndex - activeIndex);
      const direction = requestedDirection || (activeIndex === previousIndex
        ? 0
        : (forwardDistance <= backwardDistance ? 1 : -1));
      const previousCardIndex = cards.length > 2 ? normalizeIndex(activeIndex - 1) : -1;
      const nextCardIndex = cards.length > 1 ? normalizeIndex(activeIndex + 1) : -1;
      warmMobileArtworkRing();
      cards.forEach((card, index) => {
        const active = index === activeIndex;
        const before = index === previousCardIndex;
        const after = index === nextCardIndex;
        card.classList.toggle('is-before', before);
        card.classList.toggle('is-after', after);
        card.classList.toggle('is-distant', !active && !before && !after);
        card.classList.remove('is-swipe-preview');
        setInteractiveState(card, index === activeIndex);
      });
      carouselTabs.forEach((tab) => {
        const active = Number(tab.dataset.index) === activeIndex;
        tab.classList.toggle('is-active', active);
        if (!tab.dataset.fgSelectorClone) {
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        }
      });
      const activeCardId = cards[activeIndex]?.dataset.fgCardId;
      productPanels.forEach((panel) => {
        const active = panel.dataset.fgCardId === activeCardId;
        panel.hidden = !active;
        setInteractiveState(panel, active);
      });
      if (moveFocus) tabs[activeIndex]?.focus({ preventScroll: true });

      selector.classList.remove('is-moving-next', 'is-moving-previous');
      if (direction !== 0 && !reducedMotion.matches) {
        selector.classList.add(direction > 0 ? 'is-moving-next' : 'is-moving-previous');
        window.clearTimeout(deckMotionTimer);
        deckMotionTimer = window.setTimeout(() => {
          selector.classList.remove('is-moving-next', 'is-moving-previous');
        }, 720);
      }

      animateTray(direction);
      window.requestAnimationFrame(() => {
        centerActiveTab(direction, previousIndex, !tabCarouselReady);
        tabCarouselReady = true;
      });
    };

    const animateTo = (nextIndex, exitDirection, moveFocus = false) => {
      if (!cards.length) return;
      const normalizedIndex = normalizeIndex(nextIndex);
      if (normalizedIndex === activeIndex) {
        if (moveFocus) tabs[activeIndex]?.focus({ preventScroll: true });
        return;
      }

      const outgoingCard = cards[activeIndex];
      const carouselDirection = exitDirection < 0 ? 1 : -1;
      clearPreviews();
      clearSwipeStyles(outgoingCard);
      clearTrayDrag();
      select(normalizedIndex, moveFocus, carouselDirection);
    };

    const navigate = (step, moveFocus = false) => {
      animateTo(activeIndex + step, step > 0 ? -1 : 1, moveFocus);
    };

    const navigateFromControl = (step) => {
      navigate(step);
    };

    carouselTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const nextIndex = Number(tab.dataset.index) || 0;
        if (nextIndex === activeIndex) return;
        const forwardDistance = normalizeIndex(nextIndex - activeIndex);
        const backwardDistance = normalizeIndex(activeIndex - nextIndex);
        animateTo(nextIndex, forwardDistance <= backwardDistance ? -1 : 1);
      });
    });

    tabs.forEach((tab) => {
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') animateTo(0, 1, true);
        else if (event.key === 'End') animateTo(cards.length - 1, -1, true);
        else navigate(event.key === 'ArrowRight' ? 1 : -1, true);
      });
    });

    previousButton?.addEventListener('click', () => navigateFromControl(-1));
    nextButton?.addEventListener('click', () => navigateFromControl(1));

    if (swipeDeck) {
      const showSwipePreview = (deltaX) => {
        clearPreviews();
        if (Math.abs(deltaX) < 10 || cards.length < 2) return;
        const previewIndex = normalizeIndex(activeIndex + (deltaX < 0 ? 1 : -1));
        cards[previewIndex]?.classList.add('is-swipe-preview');
      };

      const finishDrag = (event, cancelled = false) => {
        if (!dragState || event.pointerId !== dragState.pointerId) return;
        const state = dragState;
        dragState = undefined;

        if (state.captured && state.card.hasPointerCapture?.(state.pointerId)) {
          state.card.releasePointerCapture(state.pointerId);
        }

        const elapsed = Math.max(performance.now() - state.startedAt, 1);
        const velocityX = state.deltaX / elapsed;
        const threshold = Math.min(92, state.card.getBoundingClientRect().width * .22);
        const shouldAdvance = !cancelled
          && state.axis === 'x'
          && (Math.abs(state.deltaX) >= threshold
            || (Math.abs(velocityX) > .48 && Math.abs(state.deltaX) > 22));

        clearPreviews();
        clearTrayDrag();
        clearSwipeStyles(state.card);

        if (state.moved) suppressCardClickUntil = performance.now() + 650;

        if (shouldAdvance) {
          const direction = state.deltaX < 0 ? -1 : 1;
          animateTo(activeIndex + (direction < 0 ? 1 : -1), direction);
          return;
        }
      };

      swipeDeck.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        const card = event.target.closest('[data-fg-selector-card]');
        if (!card || card !== cards[activeIndex]) return;

        dragState = {
          pointerId: event.pointerId,
          card,
          startX: event.clientX,
          startY: event.clientY,
          deltaX: 0,
          deltaY: 0,
          startedAt: performance.now(),
          axis: undefined,
          moved: false,
          captured: false,
        };
      });

      swipeDeck.addEventListener('pointermove', (event) => {
        if (!dragState || event.pointerId !== dragState.pointerId) return;
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        dragState.deltaX = deltaX;
        dragState.deltaY = deltaY;

        if (!dragState.axis && Math.hypot(deltaX, deltaY) > 8) {
          dragState.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.15 ? 'x' : 'y';
        }
        if (dragState.axis !== 'x') return;

        event.preventDefault();
        if (!dragState.captured) {
          dragState.card.setPointerCapture?.(event.pointerId);
          dragState.captured = true;
        }
        dragState.moved = true;
        const cardWidth = Math.max(dragState.card.getBoundingClientRect().width, 1);
        const rotation = Math.max(-12, Math.min(12, (deltaX / cardWidth) * 15));
        dragState.card.classList.add('is-dragging');
        dragState.card.style.setProperty('--fg-swipe-x', `${deltaX.toFixed(1)}px`);
        dragState.card.style.setProperty('--fg-swipe-y', `${(deltaY * .22).toFixed(1)}px`);
        dragState.card.style.setProperty('--fg-swipe-rotate', `${rotation.toFixed(2)}deg`);
        updateTrayDrag(deltaX);
        showSwipePreview(deltaX);
      });

      swipeDeck.addEventListener('pointerup', (event) => finishDrag(event));
      swipeDeck.addEventListener('pointercancel', (event) => finishDrag(event, true));
      swipeDeck.addEventListener('dragstart', (event) => event.preventDefault());
      selector.addEventListener('click', (event) => {
        if (performance.now() >= suppressCardClickUntil || !event.target.closest('[data-fg-selector-card]')) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    }

    select(activeIndex);
    window.addEventListener('resize', () => {
      window.clearTimeout(tabSnapTimer);
      window.requestAnimationFrame(() => centerActiveTab(0, activeIndex, true));
    }, { passive: true });
  };

  const initFocus = (focus) => {
    if (initialized.has(focus)) return;
    initialized.add(focus);

    const copies = [...focus.querySelectorAll('[data-fg-focus-copy]')];
    const media = [...focus.querySelectorAll('[data-fg-focus-media]')];
    const tabs = [...focus.querySelectorAll('[data-fg-focus-tab]')];
    let activeIndex = 0;

    const select = (nextIndex, moveFocus = false) => {
      if (!copies.length) return;
      activeIndex = (nextIndex + copies.length) % copies.length;
      copies.forEach((copy, index) => setInteractiveState(copy, index === activeIndex));
      media.forEach((item, index) => setInteractiveState(item, index === activeIndex));
      tabs.forEach((tab, index) => {
        const active = index === activeIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (moveFocus) tabs[activeIndex]?.focus({ preventScroll: true });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => select(Number(tab.dataset.index) || 0));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') select(0, true);
        else if (event.key === 'End') select(copies.length - 1, true);
        else select(activeIndex + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1), true);
      });
    });
    select(0);
  };

  let revealObserver;
  const initReveals = (root) => {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elementsWithin(root, '[data-fg-reveal]').forEach((element) => element.classList.add('is-visible'));
      return;
    }

    revealObserver ||= new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .14, rootMargin: '0px 0px -6% 0px' });

    elementsWithin(root, '[data-fg-reveal]').forEach((element) => {
      if (element.dataset.fgRevealReady) return;
      element.dataset.fgRevealReady = 'true';
      revealObserver.observe(element);
    });
  };

  const init = (root = document) => {
    elementsWithin(root, '[data-fg-hero]').forEach(initHero);
    elementsWithin(root, '[data-fg-editorial]').forEach(initEditorial);
    elementsWithin(root, '[data-fg-selector]').forEach(initSelector);
    elementsWithin(root, '[data-fg-focus]').forEach(initFocus);
    initReveals(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    elementsWithin(event.target, '[data-fg-editorial]').forEach((editorial) => {
      editorialCleanups.get(editorial)?.();
      editorialCleanups.delete(editorial);
    });
  });
})();
